// @flow
import React from "react";
import { connect } from "react-redux";
import {
  CarouselProvider,
  Slider,
  Slide,
  Dot,
  DotGroup,
  ButtonBack,
  ButtonNext
} from "pure-react-carousel";
import { MediaObject } from "../../flowtypes/media";
import Vimeo from "../Vimeo/index";
import { getLivePreviewUrl } from "../../helpers/media";
import CrossFadeImage from "../CrossFadeImage";
import { type $AttributeUnionType } from "../../types/attributes";

import "./PhotoSlider.css";
import { livePreviewAttributesSelector, skuSelector } from "../../store/modules/product";
import { attributesSelector } from "../../store/modules/attributes";
import Show from "../../functional/Show";

type PhotoSliderProps = {
  photos: Array<MediaObject>,
  attributes: Array<$AttributeUnionType>,
  sku: string,
  mediaUrl: string,
  livePreviewAttributes: Array<string>
};

const ArrowIcon = ({ direction }) => {
  let style;

  switch (direction) {
    case "next":
      style = { transform: "rotate(90deg)" };
      break;
    case "back":
      style = { transform: "rotate(270deg)" };
      break;
    default:
      style = { transform: "rotate(90deg)" };
      break;
  }

  return (
    <svg
      style={style}
      width="24px"
      height="24px"
      viewBox="0 0 24 24"
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g id="Symbols" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
        <g id="icon/up/black">
          <g id="icon_back-arrow">
            <polygon
              id="Shape"
              fill="#000000"
              fillRule="nonzero"
              transform="translate(11.596500, 10.485500) rotate(90.000000) translate(-11.596500, -10.485500) "
              points="15.486 2 7 10.485 7.708 11.192 15.486 18.971 16.193 18.264 8.415 10.485 16.193 2.707"
            />
            <rect id="Rectangle-15" x="0" y="0" width="24" height="24" />
          </g>
        </g>
      </g>
    </svg>
  );
};

const renderMedia = (media: MediaObject, size: "img" | "thumb" | "full" = "img") =>
  media.videoUrl ? (
    <Vimeo videoId={media.videoId} />
  ) : (
    <img alt={media.caption} src={`${media[size]}?auto=webp&width=1000`} />
  );

export class PhotoSlider extends React.Component<PhotoSliderProps> {
  // Need this to prevent the Carousel resetting whenever the attributes change.
  shouldComponentUpdate(nextProps: PhotoSliderProps) {
    const { sku, attributes, livePreviewAttributes } = nextProps;
    return getLivePreviewUrl(sku, attributes, livePreviewAttributes).isSome();
  }

  render() {
    const { photos, attributes, sku, livePreviewAttributes, mediaUrl } = this.props;

    const livePreviewUrlOption = getLivePreviewUrl(sku, attributes, livePreviewAttributes);

    const slideCount = photos.length + (livePreviewUrlOption.isSome() ? 1 : 0);
    const currentSlide = livePreviewUrlOption.isSome() ? slideCount - 1 : 0;
    return photos.length > 1 ? (
      <CarouselProvider
        naturalSlideWidth={1000}
        naturalSlideHeight={1000}
        totalSlides={slideCount}
        currentSlide={currentSlide}
        dragEnabled={false}
        touchEnabled
        key={sku}
      >
        <div className="photo-slider">
          <Slider className="photo-slider__slides">
            {photos
              .map((photo, i) => (
                <Slide index={i} key={i}>
                  {renderMedia(photo)}
                </Slide>
              ))
              .concat(
                livePreviewUrlOption
                  .map(url => (
                    <Slide index={slideCount - 1} key="livePreview">
                      <CrossFadeImage alt="Live Preview" src={`${mediaUrl}livepreview/${url}`} />
                    </Slide>
                  ))
                  .getOrElseValue([])
              )}
          </Slider>
          <div className="photo-slider__thumbs">
            <ButtonBack className="PDPRedesign--PhotoSlider-Arrow">
              <ArrowIcon direction="back" />
            </ButtonBack>
            {photos
              .map((photo, i) => {
                const isVideo = photo.thumb.includes("video");

                return (
                  <Dot
                    slide={i}
                    key={i}
                    className={`${isVideo ? "video-thumbnail-container" : ""} photo-slider__thumb`}
                  >
                    <img alt="" src={`${photo.thumb}?auto=webp&width=75`} />
                    <Show when={isVideo}>
                      <div className="play-btn--thumb">
                        <img
                          alt="Product Demo Video"
                          title="Product Demo Video"
                          src="https://cdn.artifactuprising.com/assets/pdp+images/play-btn--thumb.png"
                        />
                      </div>
                    </Show>
                  </Dot>
                );
              })
              .concat(
                livePreviewUrlOption
                  .map(url => (
                    <Dot
                      slide={slideCount - 1}
                      key="livePreview-dot"
                      className="photo-slider__thumb"
                    >
                      <CrossFadeImage
                        alt="Live Preview Thumb"
                        src={`${mediaUrl}livepreview/${url}`}
                      />
                    </Dot>
                  ))
                  .getOrElseValue([])
              )}
            <ButtonNext className="PDPRedesign--PhotoSlider-Arrow">
              <ArrowIcon direction="next" />
            </ButtonNext>
          </div>
        </div>
        <DotGroup />
      </CarouselProvider>
    ) : (
      renderMedia(photos[0], "full")
    );
  }
}

const mapStateToProps = state => ({
  attributes: attributesSelector(state),
  sku: skuSelector(state),
  livePreviewAttributes: livePreviewAttributesSelector(state)
});

export default connect(mapStateToProps)(PhotoSlider);



// WEBPACK FOOTER //
// ./src/components/PhotoSlider/index.js